<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import {
  CUSTOM_ROLE_PERMISSION_GROUPS,
  MANAGE_ALL_CONVERSATION_PERMISSIONS,
  CONVERSATION_UNASSIGNED_PERMISSIONS,
  CONVERSATION_PARTICIPATING_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'edit'].includes(value),
  },
  selectedRole: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const name = ref('');
const description = ref('');
const selectedPermissions = ref([]);

const nameInput = ref(null);

const addCustomRole = reactive({
  showLoading: false,
  message: '',
});

const rules = computed(() => ({
  name: { required, minLength: minLength(2) },
  description: { required },
  selectedPermissions: { required, minLength: minLength(1) },
}));

const v$ = useVuelidate(rules, { name, description, selectedPermissions });

const resetForm = () => {
  name.value = '';
  description.value = '';
  selectedPermissions.value = [];
  v$.value.$reset();
};

const populateEditForm = () => {
  name.value = props.selectedRole.name || '';
  description.value = props.selectedRole.description || '';
  selectedPermissions.value = props.selectedRole.permissions || [];
};

watch(
  selectedPermissions,
  (newValue, oldValue) => {
    // Check if manage all conversation permission is added or removed
    const hasAddedManageAllConversation =
      newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);
    const hasRemovedManageAllConversation =
      oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);

    if (hasAddedManageAllConversation) {
      // If manage all conversation permission is added,
      // then add unassigned and participating permissions automatically
      selectedPermissions.value = [
        ...new Set([
          ...selectedPermissions.value,
          CONVERSATION_UNASSIGNED_PERMISSIONS,
          CONVERSATION_PARTICIPATING_PERMISSIONS,
        ]),
      ];
    } else if (hasRemovedManageAllConversation) {
      // If manage all conversation permission is removed,
      // then only remove manage all conversation permission
      selectedPermissions.value = selectedPermissions.value.filter(
        p => p !== MANAGE_ALL_CONVERSATION_PERMISSIONS
      );
    }
  },
  { deep: true }
);

onMounted(() => {
  if (props.mode === 'edit') {
    populateEditForm();
  }
  // Focus the name input when mounted
  nameInput.value?.focus();
});

const getTranslationKey = base => {
  return props.mode === 'edit'
    ? `CUSTOM_ROLE.EDIT.${base}`
    : `CUSTOM_ROLE.ADD.${base}`;
};

const modalTitle = computed(() => t(getTranslationKey('TITLE')));
const modalDescription = computed(() => t(getTranslationKey('DESC')));
const submitButtonText = computed(() => t(getTranslationKey('SUBMIT')));
const permissionGroups = computed(() =>
  CUSTOM_ROLE_PERMISSION_GROUPS.map(group => ({
    ...group,
    title: t(`CUSTOM_ROLE.PERMISSION_GROUPS.${group.key}.TITLE`),
    description: t(`CUSTOM_ROLE.PERMISSION_GROUPS.${group.key}.DESCRIPTION`),
    items: group.permissions.map(permission => ({
      key: permission,
      label: t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`),
      description: t(
        `CUSTOM_ROLE.PERMISSION_DESCRIPTIONS.${permission.toUpperCase()}`
      ),
    })),
  }))
);
const selectedPermissionCount = computed(() => selectedPermissions.value.length);

const togglePermission = permission => {
  if (selectedPermissions.value.includes(permission)) {
    selectedPermissions.value = selectedPermissions.value.filter(
      item => item !== permission
    );
    return;
  }

  selectedPermissions.value = [...selectedPermissions.value, permission];
};

const handleCustomRole = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  addCustomRole.showLoading = true;
  try {
    const roleData = {
      name: name.value,
      description: description.value,
      permissions: selectedPermissions.value,
    };

    if (props.mode === 'edit') {
      await store.dispatch('customRole/updateCustomRole', {
        id: props.selectedRole.id,
        ...roleData,
      });
      useAlert(t('CUSTOM_ROLE.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('customRole/createCustomRole', roleData);
      useAlert(t('CUSTOM_ROLE.ADD.API.SUCCESS_MESSAGE'));
    }

    resetForm();
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.message || t(`CUSTOM_ROLE.FORM.API.ERROR_MESSAGE`);
    useAlert(errorMessage);
  } finally {
    addCustomRole.showLoading = false;
  }
};

const isSubmitDisabled = computed(
  () => v$.value.$invalid || addCustomRole.showLoading
);
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="modalTitle"
      :header-content="modalDescription"
    />
    <form class="flex flex-col w-full" @submit.prevent="handleCustomRole">
      <div class="w-full">
        <label :class="{ error: v$.name.$error }">
          {{ $t('CUSTOM_ROLE.FORM.NAME.LABEL') }}
          <input
            ref="nameInput"
            v-model.trim="name"
            type="text"
            :placeholder="$t('CUSTOM_ROLE.FORM.NAME.PLACEHOLDER')"
            @blur="v$.name.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.description.$error }">
          {{ $t('CUSTOM_ROLE.FORM.DESCRIPTION.LABEL') }}

          <textarea
            v-model="description"
            :rows="3"
            :placeholder="$t('CUSTOM_ROLE.FORM.DESCRIPTION.PLACEHOLDER')"
            @blur="v$.description.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <div class="flex items-center justify-between gap-3">
          <label :class="{ 'text-n-ruby-9': v$.selectedPermissions.$error }">
            {{ $t('CUSTOM_ROLE.FORM.PERMISSIONS.LABEL') }}
          </label>
          <span class="text-xs font-medium text-n-slate-10">
            {{
              $t('CUSTOM_ROLE.FORM.PERMISSIONS.COUNT', {
                selected: selectedPermissionCount,
              })
            }}
          </span>
        </div>

        <p class="mt-1 text-sm text-n-slate-10">
          {{ $t('CUSTOM_ROLE.FORM.PERMISSIONS.HELPER') }}
        </p>

        <div class="mb-4 mt-3 space-y-4">
          <section
            v-for="group in permissionGroups"
            :key="group.key"
            class="rounded-2xl border border-n-weak bg-n-surface-1 p-4"
          >
            <div class="mb-3">
              <h4 class="text-sm font-semibold text-n-slate-12">
                {{ group.title }}
              </h4>
              <p class="mt-1 text-sm text-n-slate-10">
                {{ group.description }}
              </p>
            </div>

            <div class="grid gap-2">
              <label
                v-for="permission in group.items"
                :key="permission.key"
                :for="permission.key"
                class="flex cursor-pointer items-start gap-3 rounded-xl border border-n-weak bg-white/80 p-3 transition hover:border-n-brand/40 hover:bg-n-alpha-2 dark:bg-n-solid-2/70"
                :class="{
                  'border-n-brand bg-n-alpha-2 shadow-sm dark:bg-n-solid-3/80':
                    selectedPermissions.includes(permission.key),
                }"
              >
                <input
                  :id="permission.key"
                  :checked="selectedPermissions.includes(permission.key)"
                  type="checkbox"
                  :value="permission.key"
                  name="permissions"
                  class="mt-1 h-4 w-4 shrink-0"
                  @change="togglePermission(permission.key)"
                />

                <div class="min-w-0">
                  <p class="text-sm font-medium text-n-slate-12">
                    {{ permission.label }}
                  </p>
                  <p class="mt-1 text-sm text-n-slate-10">
                    {{ permission.description }}
                  </p>
                </div>
              </label>
            </div>
          </section>
        </div>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('CUSTOM_ROLE.FORM.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="submitButtonText"
          :disabled="isSubmitDisabled"
          :is-loading="addCustomRole.showLoading"
        />
      </div>
    </form>
  </div>
</template>
